import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { setAuthToken as setServicesAuthToken } from "../services/api";

const AppContext = createContext();
const API_URL = "https://api.olympicbarbacena.com.br";
const APP_URL = "https://app.olympicbarbacena.com.br";

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const path = window.location.pathname;
    if (error.response?.status === 401 && path !== '/login' && !path.startsWith('/reset-password')) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const AppProvider = ({ children }) => {
  const [state, setState] = useState({
    user: null,
    token: null,
    isAuthenticated: false,
    associados: [],
    loading: false,
    error: null,
    socket: null,
    onlineUsers: [],
    initialized: false,
    realTimeSaldo: null,
    socketConnected: false,
    notifications: {}
  });

  const socketRef = useRef(null);

  useEffect(() => {
    const validateSession = async () => {
      if (window.location.pathname === '/login' || window.location.pathname.startsWith('/reset-password')) {
        setState(prev => ({ ...prev, initialized: true }));
        return;
      }
      try {
        const response = await axios.get(`${APP_URL}/api/auth/me`, {
          withCredentials: true,
          timeout: 15000
        });
        const token = response.data?.token || null;
        if (token) {
          setServicesAuthToken(token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        setState(prev => ({
          ...prev,
          user: response.data?.data?.user,
          token,
          isAuthenticated: true,
          initialized: true,
          realTimeSaldo: response.data?.data?.user?.saldo || null
        }));
      } catch (error) {
        setServicesAuthToken(null);
        delete axios.defaults.headers.common['Authorization'];
        setState(prev => ({
          ...prev,
          user: null,
          token: null,
          isAuthenticated: false,
          initialized: true,
          realTimeSaldo: null
        }));
      }
    };
    validateSession();
  }, []);

  useEffect(() => {
    if (state.token && state.user && state.initialized && !socketRef.current) {
      const initSocket = async () => {
        let clientIp = 'unknown';
        try {
          clientIp = await fetch(`${APP_URL}/api/client-ip`).then(r => r.json()).then(d => d.ip);
        } catch {}

        const socket = io(APP_URL, {
          auth: { token: state.token, clientIp },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000
        });

        socketRef.current = socket;

        socket.on("connect", () => {
          setState(prev => ({ ...prev, socketConnected: true }));
        });

        socket.on("disconnect", () => {
          setState(prev => ({ ...prev, socketConnected: false }));
        });

        socket.on("connect_error", () => {
          setState(prev => ({ ...prev, socketConnected: false }));
        });

        socket.on('reconnect_attempt', () => {
          socket.io.opts.auth = { token: state.token };
        });

        socket.on("saldo:update", (data) => {
          setState(prev => ({
            ...prev,
            realTimeSaldo: data.saldo,
            user: prev.user ? { ...prev.user, saldo: data.saldo } : null
          }));
        });

        socket.on("updateOnlineUsers", (users) => setState(prev => ({ ...prev, onlineUsers: users })));

        socket.on('chat:message', (data) => {
          // Só conta mensagens de MEMBRO (resposta da secretaria/transmissão não gera badge)
          if (data.message?.sender?.role === 'member' && data.message.sender.userId !== state.user?._id) {
            setState(prev => {
              const notifications = prev.notifications || {};
              const convId = data.conversationId;
              const current = notifications[convId] || { count: 0, lastMessage: null, lastMessageAt: null };
              return {
                ...prev,
                notifications: {
                  ...notifications,
                  [convId]: {
                    count: current.count + 1,
                    lastMessage: data.message,
                    lastMessageAt: data.message.createdAt
                  }
                }
              };
            });
          }
        });

        socket.on('chat:notification', (data) => {
          const { conversationId, message } = data;
          // Só notifica mensagens de membro (a secretaria não gera badge ao responder/transmitir)
          if (message?.sender?.role && message.sender.role !== 'member') return;
          setState(prev => {
            const notifications = prev.notifications || {};
            const current = notifications[conversationId] || { count: 0, lastMessage: null, lastMessageAt: null };
            return {
              ...prev,
              notifications: {
                ...notifications,
                [conversationId]: {
                  count: current.count + 1,
                  lastMessage: message,
                  lastMessageAt: message.createdAt
                }
              }
            };
          });
        });

        socket.on('notice:new', (data) => {
          window.dispatchEvent(new CustomEvent('notice:new', { detail: data }));
        });

        setState(prev => ({ ...prev, socket }));
      };

      initSocket();

      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      };
    }
  }, [state.token, state.user, state.initialized]);

  const clearNotifications = (conversationId) => {
    setState(prev => {
      const newNot = { ...prev.notifications };
      delete newNot[conversationId];
      return { ...prev, notifications: newNot };
    });
  };

  const login = async (credentials) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await axios.post(`${APP_URL}/api/auth/login`, credentials, {
        withCredentials: true
      });

      const userData = response.data.data.user;
      const tokenData = response.data.token;

      if (tokenData) {
        setServicesAuthToken(tokenData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
      }

      setState(prev => ({
        ...prev,
        user: userData,
        token: tokenData,
        realTimeSaldo: userData?.saldo || null,
        loading: false,
        isAuthenticated: true
      }));

      return response.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Login failed",
        loading: false
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${APP_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setServicesAuthToken(null);
    delete axios.defaults.headers.common['Authorization'];
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setState({
      user: null,
      token: null,
      associados: [],
      loading: false,
      error: null,
      socket: null,
      onlineUsers: [],
      initialized: true,
      realTimeSaldo: null,
      socketConnected: false
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${APP_URL}/api/auth/me`, { withCredentials: true, timeout: 15000 });
      const userData = response.data.user || response.data.data?.user;
      setState(prev => ({
        ...prev,
        user: userData,
        realTimeSaldo: userData?.saldo || null
      }));
    } catch (error) {
      logout();
    }
  }, []);

  const refreshSaldo = useCallback(() => {
    if (socketRef.current && state.socketConnected) {
      socketRef.current.emit('saldo:get');
    } else {
      refreshUser();
    }
  }, [state.socketConnected, refreshUser]);

  const updateSaldo = useCallback((newSaldo) => {
    setState(prev => ({
      ...prev,
      realTimeSaldo: newSaldo,
      user: prev.user ? { ...prev.user, saldo: newSaldo } : null
    }));
  }, []);

  const fetchAssociados = useCallback(async (nome) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}/api/associado`, {
        params: { page: 1, limit: 2000, nome },
        withCredentials: true
      });
      setState(prev => ({ ...prev, associados: response.data.data }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Failed to load data"
      }));
      if (error.response?.status === 401) logout();
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const bulkCreateUsers = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await axios.post(`${APP_URL}/api/auth/bulk-create`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Erro ao criar usuarios em lote"
      }));
      throw error;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchDependentes = useCallback(async (id) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${APP_URL}/api/member/dependentes`, {
        params: { tituloId: id },
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Failed to load dependents"
      }));
      throw error;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (state.token && !state.user && state.initialized) {
      refreshUser();
      fetchAssociados();
    }
  }, [state.token, state.user, state.initialized, fetchAssociados, refreshUser]);

  const getCurrentSaldo = useCallback(() => {
    return state.realTimeSaldo !== null ? state.realTimeSaldo : state.user?.saldo;
  }, [state.realTimeSaldo, state.user]);

  const contextValue = {
    ...state,
    login,
    logout,
    refreshUser,
    refreshSaldo,
    updateSaldo,
    getCurrentSaldo,
    fetchAssociados,
    bulkCreateUsers,
    fetchDependentes,
    clearNotifications,

    isAuthenticated: !!state.token && !!state.user,
    hasRole: (role) => state.user?.role === role,
    isAdmin: state.user?.role === "admin",
    isStaff: state.user?.role === "staff",
    isMember: state.user?.role === "member"
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

export default AppContext;
